import { useSimulationConfiguration } from "./useSimulationConfiguration";
import Spinner from "@/components/common/spinner/Spinner";
import AlertMessage from "@/components/layout/message/AlertMessage";
import Button from "@/components/common/button/Button";
import Card from "@/components/common/card/Card";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";
import styles from "./SimulationConfiguration.module.css";
import ManagementHeader from "../admin/managementHeader/ManagementHeader";
import TestSelector from "./components/testSelector/TestSelector";
import TestSettings from "./components/testSettings/TestSettings";
import QuestionsTable from "./components/questionsTable/QuestionsTable";
import { useState, useEffect } from "react";
import type { question } from "./types.api";
import QuestionEditorModal from "./components/questionEditorModal/QuestionEditorModal";

const SimulationConfiguration: React.FC = () => {
    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    const {
        state,
        currentPage,
        paginatedQuestions,
        questionsPerPage,
        handleSessionChange,
        handleTestChange,
        handleSettingUpdate,
        handleQuestionUpdate,
        handleSave,
        setCurrentPage,
    } = useSimulationConfiguration();

    const { isLoading, isSaving, error, activeTest } = state;
    const[isModalOpen, setIsModalOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<question | null>(null);

    const handleEditContent = (q: question) => {
        setSelectedQuestion(q);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedQuestion(null);
    };

    const handleSaveFromModal = (updatedQuestion: question) => {
        handleQuestionUpdate(updatedQuestion);
        handleCloseModal();
    };

    if (isLoading) return <Spinner text="Cargando configuración..." />;

    return (
        <div className={styles["page-container"]}>
            <ManagementHeader linkTo="/facilitator-panel/simulations" />
            <main className={styles["main-content"]}>
                <h1 className={styles["page-title"]}>
                    Configuración de pruebas del simulacro
                </h1>
                <AlertMessage type="error" title="Error">
                    {error?.generalMessage}
                </AlertMessage>

                <TestSelector
                    sessions={state.simulation?.sessions ||[]}
                    selectedSessionId={state.selectedSessionId}
                    selectedTestId={state.selectedTestId}
                    onSessionChange={handleSessionChange}
                    onTestChange={handleTestChange}
                />

                {activeTest && (
                    <>
                        <TestSettings
                            settings={activeTest}
                            onUpdate={handleSettingUpdate}
                        />
                        <Card>
                            <h3 className={styles["card-title"]}>Detalle de preguntas</h3>
                            <QuestionsTable
                                questions={paginatedQuestions}
                                thematicAreas={state.thematicAreas}
                                competences={state.competences}
                                errors={state.validationErrors}
                                page={currentPage}
                                pageSize={questionsPerPage}
                                simulationType={state.simulation?.type || "presencial"}
                                onUpdate={handleQuestionUpdate}
                                onEditContent={handleEditContent}
                            />
                            {selectedQuestion && (
                                <QuestionEditorModal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    question={selectedQuestion}
                                    onSave={handleSaveFromModal}
                                />
                            )}
                            <PaginationControls
                                currentPage={currentPage}
                                totalCount={activeTest.questions.length}
                                pageSize={questionsPerPage}
                                onPageChange={setCurrentPage}
                            />
                        </Card>
                        <div className={styles["actions-container"]}>
                            <Button onClick={handleSave} isLoading={isSaving}>
                                Guardar configuración
                            </Button>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default SimulationConfiguration;